<script>
import debounce from 'lodash/debounce';
import APIGroup from '@shared/services/API/Community/Group';

export default {
  props: {
    group: {
      type: Object,
      default: null,
    },
    fetchFn: {
      type: Function,
      default: null,
    },
    addFn: {
      type: Function,
      default: null,
    },
    planLock: {
      type: String,
      default: 'EXPERT',
    },
    // Surcharge optionnelle des libellés (par clé i18n), sans toucher au wording
    // par défaut. Permet ex. sur la classe virtuelle d'afficher « participant »
    // au lieu de « membre » sans impacter la communauté.
    labels: {
      type: Object,
      default: () => ({}),
    },
  },
  i18n: {
    messages: {
      en: {
        add_member: 'Add a member',
        filter_label: 'Filter by first name, last name or email',
        no_results: 'There are no results for this search.',
        added: 'Added!',
        add: 'Add',
        previous: 'Previous',
        next: 'Next',
        close: 'Close',
      },
      fr: {
        add_member: 'Ajouter un membre',
        filter_label: 'Filtrez par prénom, nom, email ou tag',
        no_results: 'Il n\'y a pas de résultat pour cette recherche.',
        added: 'Ajouté !',
        add: 'Ajouter',
        previous: 'Précédent',
        next: 'Suivant',
        close: 'Fermer',
      },
    },
  },
  data() {
    return {
      q: '',
      isLoading: false,
      availableMembers: {
        data: [],
      },
      newMember: {
        user: null,
        customer: null,
      },
    };
  },
  computed: {
    addMemberTitle() {
      return this.label('add_member');
    },
    filterLabel() {
      return this.label('filter_label');
    },
    closeButtonText() {
      return this.label('close');
    },
    wrapperComponent() {
      return this.planLock ? 'GoProWrapper' : 'div';
    },
    wrapperProps() {
      return this.planLock ? { planLock: this.planLock, hasSep: false } : {};
    },
    hasPagination() {
      const m = this.availableMembers;
      return Boolean(
        (m.meta && m.meta.last_page > 1)
        || (m.links && (m.links.prev || m.links.next)),
      );
    },
  },
  methods: {
    label(key) {
      // Priorité à la surcharge passée en prop, sinon libellé i18n par défaut.
      return this.labels[key] != null ? this.labels[key] : this.$t(key);
    },
    addMember(member) {
      member.is_loading = true;
      const request = this.addFn
        ? this.addFn(member)
        : this.$store.dispatch('community/addMember', { member });
      request
        .then(() => {
          member.is_member = true;
          this.$showMessage.success();
          this.$emit('add', member);
        })
        .catch((e) => {
          // Communauté : laisser remonter (comportement inchangé).
          // Visio : l'adaptateur a déjà affiché son toast, on avale.
          if (!this.addFn) {
            throw e;
          }
        })
        .finally(() => {
          member.is_loading = false;
        });
    },
    search: debounce(function() {
      this.loadMembers();
    }, 500),
    loadMembers(url = null) {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      const q = this.q || undefined;
      const request = this.fetchFn
        ? this.fetchFn(url, { q })
        : APIGroup.getAvailableMembers(url || this.group.uuid, { q });
      request
        .then(this.setMembers)
        .catch((e) => {
          if (!this.fetchFn) {
            throw e;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setMembers(data) {
      data.data = data.data.map((v) => ({
        ...v,
        is_member: false,
        is_loading: false,
      }));

      this.availableMembers = data;
    },
  },
  created() {
    this.loadMembers();
  },
};
</script>

<template>
  <AppCardModal
    class="is-relative"
    hasCloseBtn
    @close="$emit('cancel')"
  >
    <template #title>
      {{ addMemberTitle }}
    </template>
    <template #body>
      <component :is="wrapperComponent" v-bind="wrapperProps">
        <div class="mh-360">
          <form @submit.prevent="search">
            <b-field :label="filterLabel">
              <b-input
                v-model="q"
                expanded
                maxlength="32"
                @input="search"
              />
            </b-field>
          </form>

          <div class="is-relative pb-5">
            <ul v-if="availableMembers.data.length > 0" class="mt-5">
              <li
                v-for="member in availableMembers.data"
                :key="member.uuid"
                class="box flex-between-center"
              >
                <div>
                  <p>
                    {{ member.firstname }} {{ member.lastname }}
                  </p>
                  <slot name="member-meta" :member="member">
                    <p class="mt-2">
                      <span
                        class="tag"
                        :class="{
                          'is-dark': member.type === 'USER',
                          'is-primary': member.type === 'CUSTOMER',
                        }"
                        v-t="`community.member_type.${member.type}`"
                      />
                    </p>
                  </slot>
                </div>
                <b-button
                  class="ml-5"
                  type="is-primary"
                  :disabled="member.is_member"
                  :loading="member.is_loading"
                  @click="addMember(member)"
                >
                  <template v-if="member.is_member">
                    {{ label('added') }}
                  </template>
                  <template v-else>
                    {{ label('add') }}
                  </template>
                </b-button>
              </li>
            </ul>
            <p v-else>
              {{ label('no_results') }}
            </p>
            <b-loading :active="isLoading" :is-full-page="false" />
          </div>

          <div
            v-if="hasPagination"
            class="buttons is-centered mt-5 pb-5"
          >
            <b-button
              :disabled="!availableMembers.links.prev"
              @click="loadMembers(availableMembers.links.prev)"
            >
              {{ label('previous') }}
            </b-button>
            <b-button
              :disabled="!availableMembers.links.next"
              @click="loadMembers(availableMembers.links.next)"
            >
              {{ label('next') }}
            </b-button>
          </div>
        </div>
      </component>
    </template>
    <template #foot>
      <b-button type="is-text" @click="$emit('cancel')">
        {{ closeButtonText }}
      </b-button>
    </template>
  </AppCardModal>
</template>
